import { styleConstants, themeConstants } from '@paywithextend/components'
import React, { ReactElement, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import SaasBuildings from 'src/assets/images/SaaSBuildings.svg'
import Box from 'src/components/Box/Box'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import ButtonAndMoreDropdown from 'src/components/ButtonAndMoreDropdown/ButtonAndMoreDropdown'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import LoadingShimmer from 'src/components/LoadingShimmer/LoadingShimmer'
import { MoreDropdownDesigns } from 'src/components/MoreDropdown/MoreDropdown'
import SvgCancelIcon from 'src/components/Svgs/SvgCancelIcon'
import SvgChevronDownLoD from 'src/components/Svgs/SvgChevronDownLoD'
import SvgChevronUpLoD from 'src/components/Svgs/SvgChevronUpLoD'
import SvgExternalLink from 'src/components/Svgs/SvgExternalLink'
import SvgPencilIcon from 'src/components/Svgs/SvgPencilIcon'
import Tag from 'src/components/Tag/Tag'
import Trns, { useTranslate } from 'src/localization/Trns'
import {
  OrganizationSaasTierResponse,
  OrganizationsListModel,
  SubscriptionStatus,
} from 'src/models/organizations.models'
import { UserListModel } from 'src/models/user.models'
import { connectedPush } from 'src/navigation/Lnk'
import {
  CANCEL_SUBSCRIPTION_ROUTE,
  upgradeSubscriptionUrl,
} from 'src/navigation/urls'
import {
  usePostEnterpriseRequest,
  usePutManageSubscription,
  usePutRenewSubscriptionRequest,
  useQueryOrganizationSubscription,
} from 'src/queries/organizations/saas.queries'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { css } from 'styled-components'

import SvgRecurring from '../Svgs/SvgRecurring'

export type ManageOrgSubscriptionProps = {
  user: UserListModel
  organization: OrganizationsListModel
  currentPlan?: OrganizationSaasTierResponse
  isLoadingCurrentPlan: boolean
  isSuccess: boolean
}

type SubscriptionContent = {
  title: ReactElement
  description: ReactElement
  button?: ReactElement
}

const ManageOrgSubscriptionBox: React.FC<ManageOrgSubscriptionProps> = (
  props,
): React.ReactElement => {
  const { mutate: postEnterpriseRequest } = usePostEnterpriseRequest()
  const dispatch = useDispatch()
  const translate = useTranslate()

  const orgId = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )?.id

  const { show_manage_subscription: showManageSubscription } = useSelector(
    selectFeatureFlaggerFromState,
  )

  const { mutate: renewSubscription } = usePutRenewSubscriptionRequest()

  const loadingContent: SubscriptionContent = {
    title: <LoadingShimmer />,
    description: <LoadingShimmer />,
    button: <LoadingShimmer />,
  }
  const [currentPlanContent, setCurrentPlanContent] =
    useState<SubscriptionContent>(loadingContent)

  const starterContent: SubscriptionContent = {
    title: <Trns source={'Starter'} />,
    description: (
      <Trns
        source={
          'You are currently on the Extend Starter plan. Click ${here} to view a full list of available plans and features. For any billing inquiries, contact us at ${billingEmail}'
        }
        expressions={{
          here: (
            <HoverableLink
              href={'https://www.paywithextend.com/pricing'}
              ariaLabelSource={'Pricing'}
            >
              <Trns source={'here'} />
            </HoverableLink>
          ),
          billingEmail: (
            <a
              css={css`
                font-size: 14px;
                color: ${themeConstants.colors.cta};
                font-family: ${styleConstants.fontFamilies.regular};
                text-decoration: underline;
              `}
              href={`mailto:billing@paywithextend.com`}
            >
              {'billing@paywithextend.com'}
            </a>
          ),
        }}
      />
    ),
    button: (
      <Button
        linkProps={{
          to: upgradeSubscriptionUrl(),
          ariaLabelSource: 'Upgrade',
        }}
        text={<Trns source={'Upgrade'} />}
        design={ButtonDesigns.designCta}
        type={ButtonTypes.button}
      />
    ),
  }

  const proContent: SubscriptionContent = {
    title: <Trns source={'Pro'} />,
    description: (
      <Trns
        source={
          'You are currently on the Extend Pro plan. Click ${here} to view a full list of available plans and features. For any billing inquiries, contact us at ${billingEmail}'
        }
        expressions={{
          here: (
            <HoverableLink
              href={'https://www.paywithextend.com/pricing'}
              ariaLabelSource={'Pricing'}
            >
              <Trns source={'here'} />
            </HoverableLink>
          ),
          billingEmail: (
            <a
              css={css`
                font-size: 14px;
                color: ${themeConstants.colors.cta};
                font-family: ${styleConstants.fontFamilies.regular};
                text-decoration: underline;
              `}
              href={`mailto:billing@paywithextend.com`}
            >
              {'billing@paywithextend.com'}
            </a>
          ),
        }}
      />
    ),
    button: (
      <Button
        onClick={(): void => {
          postEnterpriseRequest({
            orgId: orgId || '',
            body: {
              intention: 'Manage Org Subscription -> Upgrade to Enterprise',
            },
          })
        }}
        text={<Trns source={'Upgrade'} />}
        design={ButtonDesigns.designCta}
        type={ButtonTypes.button}
        css={css`
          width: 100%;
        `}
      />
    ),
  }

  const enterpriseContent: SubscriptionContent = {
    title: <Trns source={'Enterprise'} />,
    description: (
      <Trns
        source={
          'You are currently on the Extend Enterprise plan. For any billing inquiries or to further enhance your plan, contact us at ${billingEmail}'
        }
        expressions={{
          billingEmail: (
            <a
              css={css`
                font-size: 14px;
                color: ${themeConstants.colors.cta};
                font-family: ${styleConstants.fontFamilies.regular};
                text-decoration: underline;
              `}
              href={`mailto:billing@paywithextend.com`}
            >
              {'billing@paywithextend.com'}
            </a>
          ),
        }}
      />
    ),
    button: undefined,
  }

  const { mutate: putManageSubscription } = usePutManageSubscription()

  const { data: organizationSubscription } = useQueryOrganizationSubscription({
    orgId: props.organization.id,
    enabled: !!props.organization.id,
  })

  const handleCancelSubscription = (): void => {
    dispatch(connectedPush(CANCEL_SUBSCRIPTION_ROUTE(props.organization.id)))
  }

  const handleRenewSubscription = (): void => {
    renewSubscription(
      { orgId: props.organization.id, body: {} },
      {
        onSuccess: (data) => {
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <span>
                  {translate(
                    'Your subscription has been renewed successfully!',
                  )}
                </span>
              ),
            }),
          )
        },
        onError: (error) => {
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <span>
                  {translate(
                    'Oops! There was a problem renewing your subscription. Please try again later or contact customer support.',
                  )}
                </span>
              ),
              failureColorScheme: true,
            }),
          )
        },
      },
    )
  }

  useEffect(() => {
    if (props.isLoadingCurrentPlan) {
      setCurrentPlanContent(loadingContent)
      return
    }

    if (props.currentPlan) {
      switch (props.currentPlan.tier) {
        case 'STARTER':
          setCurrentPlanContent(starterContent)
          break
        case 'PRO':
          setCurrentPlanContent(proContent)
          break
        case 'ENTERPRISE':
          setCurrentPlanContent(enterpriseContent)
          break
        default:
          setCurrentPlanContent(starterContent)
          break
      }
    }
  }, [props.currentPlan?.tier, props.isLoadingCurrentPlan])

  return (
    <Box
      noPadding
      css={css`
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding: 20px;
        & > :last-child {
          width: calc(100%); // Adjust the width to account for the padding
          height: calc(
            100% + 40px
          ); // Adjust the height to account for the padding
          margin-bottom: -20px;
          border-bottom-left-radius: 10px;
          padding: 20px;
          border-bottom-right-radius: 10px;
          margin-left: -20px; // Offset the left margin by half the padding
          margin-right: -20px; // Offset the right margin by half the padding
        }
      `}
    >
      <div
        css={css`
          margin-bottom: 10px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: row;
            gap: 4px;
          `}
        >
          <span
            css={css`
              font-size: 20px;
              color: ${themeConstants.colors.font_primary};
              font-family: ${styleConstants.fontFamilies.medium};
            `}
          >
            <Trns source={'Current Plan'} />
          </span>
          <Tag
            text={currentPlanContent.title}
            testId={`tier-${(currentPlanContent.title.props.source || '').toLowerCase()}`}
          />
        </div>
        {props.currentPlan !== undefined &&
          props.currentPlan?.tier !== 'STARTER' &&
          props.user.hasOrgAdminPermissions &&
          (showManageSubscription ? (
            <ButtonAndMoreDropdown
              buttonProps={{
                onClick: (): void => {
                  putManageSubscription({
                    orgId: props.organization.id,
                    redirect: true,
                    body: {},
                  })
                },
                text: (
                  <span
                    css={css`
                      color: ${themeConstants.colors.cta};
                    `}
                  >
                    <Trns source={'Manage Plan'} />
                  </span>
                ),
                design: ButtonDesigns.plainNoShadow,
                type: ButtonTypes.button,
              }}
              moreDropdownProps={{
                design: MoreDropdownDesigns.CtaBlueWhiteBg,
                minWidth: 330,
                verticalBuffer: 8,
                topOptions: [
                  {
                    id: 'modifyPlan',
                    key: 'modifyPlan',
                    testId: 'modifyPlan',
                    label: (
                      <div
                        css={css`
                          display: flex;
                          flex-direction: row;
                          align-items: center;
                          gap: 8px;
                          left: -2px;
                          position: relative;
                          width: 100%;
                          font-size: 14px;
                          font-family: ${styleConstants.fontFamilies.regular};
                          color: ${themeConstants.colors.cta};
                        `}
                      >
                        <SvgPencilIcon />
                        <Trns source={'Modify Plan'} />
                      </div>
                    ),
                    ariaLabelSource: 'Modify Plan',
                    onClick: (): void => {
                      putManageSubscription({
                        orgId: props.organization.id,
                        redirect: true,
                        body: {},
                      })
                    },
                  },
                ],
                bottomOptions: [
                  ...(organizationSubscription?.subscription?.state.status ===
                  'ACTIVE'
                    ? [
                        {
                          id: 'cancelSubscription',
                          testId: 'cancelSubscription',
                          key: 'cancelSubscription',
                          label: (
                            <div
                              css={css`
                                display: flex;
                                flex-direction: row;
                                align-items: center;
                                gap: 8px;
                                left: -2px;
                                position: relative;
                                width: 100%;
                                font-size: 14px;
                                font-family: ${styleConstants.fontFamilies
                                  .regular};
                                color: ${styleConstants.colors.red_dark};
                              `}
                            >
                              <SvgCancelIcon />
                              {translate('Cancel Subscription')}
                            </div>
                          ),
                          ariaLabelSource: 'Cancel Subscription',
                          onClick: handleCancelSubscription,
                        },
                      ]
                    : []),
                  ...(organizationSubscription?.subscription?.state.status ===
                  SubscriptionStatus.NON_RENEWING
                    ? [
                        {
                          id: 'renewSubscription',
                          testId: 'renewSubscription',
                          key: 'renewSubscription',
                          label: (
                            <div
                              css={css`
                                display: flex;
                                flex-direction: row;
                                align-items: center;
                                gap: 8px;
                                left: -2px;
                                position: relative;
                                width: 100%;
                                font-size: 14px;
                                font-family: ${styleConstants.fontFamilies
                                  .regular};
                                color: ${themeConstants.colors.cta};
                              `}
                            >
                              <SvgRecurring height={16} width={16} />
                              {translate('Reactivate Subscription')}
                            </div>
                          ),
                          ariaLabelSource: 'Reactivate Subscription',
                          onClick: handleRenewSubscription,
                        },
                      ]
                    : []),
                ],
                openComponent: (
                  <span
                    css={css`
                      color: ${themeConstants.colors.cta};
                    `}
                  >
                    <SvgChevronUpLoD />
                  </span>
                ),
                closedComponent: (
                  <span
                    css={css`
                      color: ${themeConstants.colors.cta};
                    `}
                  >
                    <SvgChevronDownLoD />
                  </span>
                ),
              }}
            />
          ) : (
            <HoverableLink
              css={css`
                display: flex;
                align-items: center;
              `}
              ariaLabelSource={'Manage Plan'}
              colorSvgOnHover
              onClick={(): void => {
                putManageSubscription({
                  orgId: props.organization.id,
                  redirect: true,
                  body: {},
                })
              }}
            >
              <Trns source={'Manage Plan'} />
              <SvgExternalLink
                css={css`
                  transform: scale(calc(16 / 24));
                `}
              />
            </HoverableLink>
          ))}
      </div>
      <img
        src={SaasBuildings}
        height={120}
        width={120}
        alt={'Saas Buildings'}
        css={css`
          display: block;
          margin-left: auto;
          margin-right: auto;
        `}
      />
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 10px;
        `}
      >
        <span
          css={css`
            font-size: 14px;
            color: ${themeConstants.colors.font_primary};
            font-family: ${styleConstants.fontFamilies.regular};
          `}
        >
          {currentPlanContent.description}
        </span>
      </div>
      <div
        css={css`
          background-color: ${styleConstants.colors.gray_light};
        `}
      >
        {currentPlanContent.button}
      </div>
    </Box>
  )
}

export default ManageOrgSubscriptionBox
